import { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import AdminLayout from '../../../layouts/AdminLayout'
import apiClient from '@services/api/client'

function SystemSecurityPage() {
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('dashboard')
  const [stats, setStats] = useState({
    total_users: 0,
    active_sessions: 0,
    connections_today: 0,
    failed_attempts_24h: 0,
    blocked_accounts: 0,
    active_alerts: 0
  })
  const [securitySettings, setSecuritySettings] = useState({
    password_min_length: 8,
    password_require_uppercase: true,
    password_require_lowercase: true,
    password_require_numbers: true,
    password_require_special: true,
    session_timeout: 30,
    max_login_attempts: 5,
    lockout_duration: 15,
    two_factor_required: false,
    ip_whitelist_enabled: false,
    maintenance_mode: false
  })
  const [lockedAccounts, setLockedAccounts] = useState([])
  const [ipWhitelist, setIpWhitelist] = useState([])
  const [newIp, setNewIp] = useState('')
  const [systemLogs, setSystemLogs] = useState([])
  const [connections, setConnections] = useState([])
  const [activeSessions, setActiveSessions] = useState([])
  const [alerts, setAlerts] = useState([])
  const [chartData, setChartData] = useState([])
  const [topFailedIps, setTopFailedIps] = useState([])

  useEffect(() => {
    loadSystemSecurity()
  }, [])

  const loadSystemSecurity = async () => {
    setLoading(true)
    try {
      // Charger les statistiques
      try {
        const r = await apiClient.get('/logs/stats')
        setStats(r.data || r)
      } catch { setStats({}) }

      // Charger les paramètres de sécurité
      try {
        const r = await apiClient.get('/logs/security-settings')
        if (r.settings) setSecuritySettings(r.settings)
      } catch {}

      // Charger les connexions
      try {
        const r = await apiClient.get('/logs/connections')
        setConnections(r.data || [])
      } catch { setConnections([]) }

      // Charger les sessions actives
      try {
        const r = await apiClient.get('/logs/active-sessions')
        setActiveSessions(r.data || [])
      } catch { setActiveSessions([]) }

      // Charger les comptes verrouillés
      try {
        const r = await apiClient.get('/logs/locked-accounts')
        setLockedAccounts(r.data || [])
      } catch { setLockedAccounts([]) }

      // Charger les alertes
      try {
        const r = await apiClient.get('/logs')
        setAlerts(r.data || [])
      } catch { setAlerts([]) }

      // Charger la liste blanche IP
      try {
        const r = await apiClient.get('/logs/ip-whitelist')
        setIpWhitelist(r.data || [])
      } catch {}

      // Charger les logs système
      try {
        const r = await apiClient.get('/logs/system')
        setSystemLogs(r.data || [])
      } catch {}

      // Charger les données du graphique
      try {
        const r = await apiClient.get('/logs/chart-data')
        setChartData(r.data || [])
      } catch {}

      // Charger les Top IPs avec échecs
      try {
        const r = await apiClient.get('/logs/top-failed-ips')
        setTopFailedIps(r.data || [])
      } catch {}
    } catch (error) {
      console.error('Erreur chargement sécurité système:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveSettings = async () => {
    try {
      await apiClient.post('/logs/security-settings', securitySettings)
      toast.success('Paramètres de sécurité système mis à jour')
    } catch {
      toast.error('Fonctionnalité bientôt disponible')
    }
  }

  const handleUnlockAccount = async (userId) => {
    try {
      await apiClient.post('/logs/unlock-account', { user_id: userId })
      toast.success('Compte déverrouillé')
      loadSystemSecurity()
    } catch {
      toast.error('Fonctionnalité bientôt disponible')
    }
  }

  const handleAddIp = async () => {
    if (!newIp) {
      toast.error('Veuillez entrer une adresse IP')
      return
    }

    try {
      await apiClient.post('/logs/ip-whitelist', { ip: newIp })
      toast.success('IP ajoutée à la liste blanche')
      setNewIp('')
      loadSystemSecurity()
    } catch {
      toast.error('Fonctionnalité bientôt disponible')
    }
  }

  const handleRemoveIp = async (ip) => {
    try {
      await apiClient.post('/logs/ip-whitelist/remove', { ip })
      toast.success('IP retirée de la liste blanche')
      loadSystemSecurity()
    } catch {
      toast.error('Fonctionnalité bientôt disponible')
    }
  }

  return (
    <AdminLayout>
      <div className="space-y-6 mt-10">
        {/* Header */}
        <div className="bg-white rounded-md border border-slate-200 p-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-800">Sécurité du Système</h1>
              <p className="text-gray-500 mt-2 flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Surveillance et gestion de la sécurité
              </p>
            </div>
            <button
              onClick={loadSystemSecurity}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FFD700] to-[#F5C800] text-[#002A4E] rounded-md transition-all font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Actualiser
            </button>
          </div>
        </div>

        {/* Statistiques principales */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {/* Utilisateurs */}
          <div className="bg-white rounded-md p-6 border border-slate-200 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Utilisateurs</p>
                <p className="text-2xl font-bold text-gray-800">{stats.total_users}</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Sessions actives */}
          <div className="bg-white rounded-md p-6 border border-slate-200 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Sessions</p>
                <p className="text-2xl font-bold text-green-600">{stats.active_sessions}</p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Connexions aujourd'hui */}
          <div className="bg-white rounded-md p-6 border border-slate-200 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Connexions/Jour</p>
                <p className="text-2xl font-bold text-indigo-600">{stats.connections_today}</p>
              </div>
              <div className="w-12 h-12 bg-indigo-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
              </div>
            </div>
          </div>

          {/* Échecs 24h */}
          <div className="bg-white rounded-md p-6 border border-slate-200 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Échecs (24h)</p>
                <p className="text-2xl font-bold text-red-600">{stats.failed_attempts_24h}</p>
              </div>
              <div className="w-12 h-12 bg-red-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Comptes bloqués */}
          <div className="bg-white rounded-md p-6 border border-slate-200 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Bloqués</p>
                <p className="text-2xl font-bold text-orange-600">{stats.blocked_accounts}</p>
              </div>
              <div className="w-12 h-12 bg-orange-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
            </div>
          </div>

          {/* Alertes */}
          <div className="bg-white rounded-md p-6 border border-slate-200 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Alertes</p>
                <p className="text-2xl font-bold text-purple-600">{stats.active_alerts}</p>
              </div>
              <div className="w-12 h-12 bg-purple-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="mb-6 bg-white rounded-md border border-slate-200 p-2">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all ${
                activeTab === 'dashboard'
                  ? 'bg-red-500 text-white '
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
              </svg>
              Tableau de bord
            </button>
            <button
              onClick={() => setActiveTab('connections')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all ${
                activeTab === 'connections'
                  ? 'bg-red-500 text-white '
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Connexions
            </button>
            <button
              onClick={() => setActiveTab('sessions')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all ${
                activeTab === 'sessions'
                  ? 'bg-red-500 text-white '
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
              Sessions
            </button>
            <button
              onClick={() => setActiveTab('blocages')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all ${
                activeTab === 'blocages'
                  ? 'bg-red-500 text-white '
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              Blocages
              {lockedAccounts.length > 0 && (
                <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-xs font-bold">
                  {lockedAccounts.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('alertes')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all ${
                activeTab === 'alertes'
                  ? 'bg-red-500 text-white '
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Alertes
            </button>
            <button
              onClick={() => setActiveTab('parametres')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all ${
                activeTab === 'parametres'
                  ? 'bg-red-500 text-white '
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Paramètres
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all ${
                activeTab === 'logs'
                  ? 'bg-red-500 text-white '
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Logs système
            </button>
          </div>
        </div>      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5B700]"></div>
        </div>
      ) : (
        <>
          {/* Tableau de bord */}
          {activeTab === 'dashboard' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Graphique activité 24h */}
              <div className="bg-white rounded-md border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <h2 className="text-lg font-semibold text-gray-800">Activité des dernières 24h</h2>
                </div>
                <div className="h-64">
                  {chartData.length > 0 ? (
                    <div className="h-full flex flex-col justify-end px-2">
                      <div className="flex items-end justify-around h-56 gap-2">
                        {chartData.map((data, index) => {
                          const maxValue = Math.max(...chartData.map(d => Math.max(d.successful, d.failed)))
                          const successHeight = maxValue > 0 ? (data.successful / maxValue) * 100 : 0
                          const failedHeight = maxValue > 0 ? (data.failed / maxValue) * 100 : 0
                          
                          return (
                            <div key={index} className="flex-1 flex flex-col items-center gap-1">
                              <div className="w-full flex gap-1 items-end h-48">
                                <div 
                                  className="flex-1 bg-green-500 rounded-t transition-all hover:bg-green-600"
                                  style={{ height: `${successHeight}%` }}
                                  title={`${data.successful} connexions réussies`}
                                ></div>
                                <div 
                                  className="flex-1 bg-red-500 rounded-t transition-all hover:bg-red-600"
                                  style={{ height: `${failedHeight}%` }}
                                  title={`${data.failed} tentatives échouées`}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-500 font-medium">{data.time}</span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-400">
                      <p className="text-sm">Aucune donnée disponible</p>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-center gap-6 mt-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded"></div>
                    <span className="text-gray-600">Connexions réussies</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded"></div>
                    <span className="text-gray-600">Tentatives échouées</span>
                  </div>
                </div>
              </div>

              {/* Top IPs avec échecs */}
              <div className="bg-white rounded-md border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <h2 className="text-lg font-semibold text-gray-800">Top IPs avec échecs (7 jours)</h2>
                </div>
                <div className="space-y-3">
                  {topFailedIps.length > 0 ? (
                    topFailedIps.map((ipData, index) => {
                      const maxCount = Math.max(...topFailedIps.map(ip => ip.count))
                      const percentage = maxCount > 0 ? (ipData.count / maxCount) * 100 : 0
                      
                      return (
                        <div key={index} className="flex items-center gap-3">
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-mono text-gray-700">{ipData.ip}</span>
                              <span className="text-xs font-semibold text-red-600">{ipData.count} échecs</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-red-400 to-red-600 h-2 rounded-full transition-all"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <p className="text-sm">Aucune activité suspecte détectée</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Connexions */}
          {activeTab === 'connections' && (
            <div className="bg-white rounded-md border border-slate-200">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-[#002A4E]">Historique des connexions</h2>
                <button
                  onClick={loadSystemSecurity}
                  className="text-[#B8860B] hover:text-[#B8860B] font-medium text-sm flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Actualiser
                </button>
              </div>
              <div className="p-6">
                {connections.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Utilisateur</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">IP</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Statut</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {connections.map((conn, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 text-sm text-gray-800">{conn.user_name}</td>
                            <td className="py-3 px-4 text-sm font-mono text-gray-600">{conn.ip_address}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                conn.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {conn.success ? 'Réussie' : 'Échouée'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600">{new Date(conn.timestamp).toLocaleString('fr-FR')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <p className="text-sm">Aucune connexion enregistrée</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sessions */}
          {activeTab === 'sessions' && (
            <div className="bg-white rounded-md border border-slate-200">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-semibold text-[#002A4E]">Sessions actives</h2>
              </div>
              <div className="p-6">
                {activeSessions.length > 0 ? (
                  <div className="space-y-3">
                    {activeSessions.map((session, index) => (
                      <div key={index} className="flex items-center justify-between p-4 border border-gray-100 rounded-md hover:bg-gray-50">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-green-50 rounded-md flex items-center justify-center">
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{session.user_name}</p>
                            <p className="text-sm text-gray-600">{session.ip_address} • {session.device}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Depuis {new Date(session.start_time).toLocaleString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        <button className="px-3 py-1 bg-red-100 text-red-700 hover:bg-red-200 rounded-md text-sm font-medium">
                          Terminer
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <p className="text-sm">Aucune session active</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Blocages (anciennement accounts) */}
          {activeTab === 'blocages' && (
            <div className="bg-white rounded-md border border-slate-200">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-semibold text-[#002A4E]">Comptes verrouillés</h2>
              </div>
              <div className="p-6">
                {lockedAccounts.length > 0 ? (
                  <div className="space-y-3">
                    {lockedAccounts.map((account, index) => (
                      <div key={index} className="flex items-center justify-between p-4 border border-gray-100 rounded-md hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-red-50 rounded-md flex items-center justify-center">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{account.name}</p>
                            <p className="text-sm text-gray-600">{account.email}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Verrouillé le: {new Date(account.locked_at).toLocaleString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleUnlockAccount(account.id)}
                          className="px-4 py-2 bg-green-100 text-green-700 hover:bg-green-200 rounded-md font-medium transition-colors"
                        >
                          Déverrouiller
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <svg className="w-16 h-16 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm">Aucun compte verrouillé</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Alertes */}
          {activeTab === 'alertes' && (
            <div className="bg-white rounded-md border border-slate-200">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-semibold text-[#002A4E]">Alertes de sécurité</h2>
              </div>
              <div className="p-6">
                {alerts.length > 0 ? (
                  <div className="space-y-3">
                    {alerts.map((alert, index) => (
                      <div key={index} className={`p-4 rounded-md border-l-4 ${
                        alert.severity === 'high' ? 'bg-red-50 border-red-500' :
                        alert.severity === 'medium' ? 'bg-orange-50 border-orange-500' :
                        'bg-yellow-50 border-yellow-500'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-gray-800">{alert.title}</p>
                            <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                            <p className="text-xs text-gray-500 mt-2">{new Date(alert.timestamp).toLocaleString('fr-FR')}</p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            alert.severity === 'high' ? 'bg-red-100 text-red-700' :
                            alert.severity === 'medium' ? 'bg-orange-100 text-orange-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {alert.severity === 'high' ? 'Élevée' : alert.severity === 'medium' ? 'Moyenne' : 'Faible'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <svg className="w-16 h-16 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm">Aucune alerte active</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Paramètres (anciennement settings) */}
          {activeTab === 'parametres' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Politique de mot de passe */}
              <div className="bg-white rounded-md border border-slate-200">
                <div className="p-6 border-b border-gray-100">
                  <h2 className="text-xl font-semibold text-[#002A4E]">Politique de mot de passe</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Longueur minimale: {securitySettings.password_min_length} caractères
                    </label>
                    <input
                      type="range"
                      min="6"
                      max="20"
                      value={securitySettings.password_min_length}
                      onChange={(e) => setSecuritySettings({...securitySettings, password_min_length: parseInt(e.target.value)})}
                      className="w-full"
                    />
                  </div>

                  <label className="flex items-center gap-3 p-3 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={securitySettings.password_require_uppercase}
                      onChange={(e) => setSecuritySettings({...securitySettings, password_require_uppercase: e.target.checked})}
                      className="w-5 h-5 text-[#B8860B] border-gray-300 rounded focus:ring-amber-200"
                    />
                    <span className="text-sm text-gray-700">Majuscules obligatoires (A-Z)</span>
                  </label>

                  <label className="flex items-center gap-3 p-3 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={securitySettings.password_require_lowercase}
                      onChange={(e) => setSecuritySettings({...securitySettings, password_require_lowercase: e.target.checked})}
                      className="w-5 h-5 text-[#B8860B] border-gray-300 rounded focus:ring-amber-200"
                    />
                    <span className="text-sm text-gray-700">Minuscules obligatoires (a-z)</span>
                  </label>

                  <label className="flex items-center gap-3 p-3 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={securitySettings.password_require_numbers}
                      onChange={(e) => setSecuritySettings({...securitySettings, password_require_numbers: e.target.checked})}
                      className="w-5 h-5 text-[#B8860B] border-gray-300 rounded focus:ring-amber-200"
                    />
                    <span className="text-sm text-gray-700">Chiffres obligatoires (0-9)</span>
                  </label>

                  <label className="flex items-center gap-3 p-3 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={securitySettings.password_require_special}
                      onChange={(e) => setSecuritySettings({...securitySettings, password_require_special: e.target.checked})}
                      className="w-5 h-5 text-[#B8860B] border-gray-300 rounded focus:ring-amber-200"
                    />
                    <span className="text-sm text-gray-700">Caractères spéciaux (!@#$%^&*)</span>
                  </label>
                </div>
              </div>

              {/* Sécurité des sessions */}
              <div className="bg-white rounded-md border border-slate-200">
                <div className="p-6 border-b border-gray-100">
                  <h2 className="text-xl font-semibold text-[#002A4E]">Sécurité des sessions</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Timeout session: {securitySettings.session_timeout} minutes
                    </label>
                    <input
                      type="range"
                      min="5"
                      max="120"
                      step="5"
                      value={securitySettings.session_timeout}
                      onChange={(e) => setSecuritySettings({...securitySettings, session_timeout: parseInt(e.target.value)})}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tentatives max: {securitySettings.max_login_attempts}
                    </label>
                    <input
                      type="range"
                      min="3"
                      max="10"
                      value={securitySettings.max_login_attempts}
                      onChange={(e) => setSecuritySettings({...securitySettings, max_login_attempts: parseInt(e.target.value)})}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Durée verrouillage: {securitySettings.lockout_duration} minutes
                    </label>
                    <input
                      type="range"
                      min="5"
                      max="60"
                      step="5"
                      value={securitySettings.lockout_duration}
                      onChange={(e) => setSecuritySettings({...securitySettings, lockout_duration: parseInt(e.target.value)})}
                      className="w-full"
                    />
                  </div>

                  <label className="flex items-center gap-3 p-3 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={securitySettings.two_factor_required}
                      onChange={(e) => setSecuritySettings({...securitySettings, two_factor_required: e.target.checked})}
                      className="w-5 h-5 text-[#B8860B] border-gray-300 rounded focus:ring-amber-200"
                    />
                    <span className="text-sm text-gray-700">2FA obligatoire pour tous</span>
                  </label>

                  <label className="flex items-center gap-3 p-3 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={securitySettings.ip_whitelist_enabled}
                      onChange={(e) => setSecuritySettings({...securitySettings, ip_whitelist_enabled: e.target.checked})}
                      className="w-5 h-5 text-[#B8860B] border-gray-300 rounded focus:ring-amber-200"
                    />
                    <span className="text-sm text-gray-700">Activer liste blanche IP</span>
                  </label>
                </div>
              </div>

              {/* Mode maintenance */}
              <div className="bg-white rounded-md border border-slate-200 lg:col-span-2">
                <div className="p-6 border-b border-gray-100">
                  <h2 className="text-xl font-semibold text-[#002A4E]">Mode maintenance</h2>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-md flex items-center justify-center ${
                        securitySettings.maintenance_mode ? 'bg-orange-100' : 'bg-green-100'
                      }`}>
                        <svg className={`w-6 h-6 ${securitySettings.maintenance_mode ? 'text-orange-600' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">
                          {securitySettings.maintenance_mode ? 'Mode maintenance actif' : 'Système opérationnel'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {securitySettings.maintenance_mode ? 'La plateforme est inaccessible aux utilisateurs' : 'La plateforme est accessible'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSecuritySettings({...securitySettings, maintenance_mode: !securitySettings.maintenance_mode})}
                      className={`px-4 py-2 rounded-md font-medium transition-colors ${
                        securitySettings.maintenance_mode 
                          ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                          : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                      }`}
                    >
                      {securitySettings.maintenance_mode ? 'Désactiver' : 'Activer'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <button
                  onClick={handleSaveSettings}
                  className="w-full bg-[#FFD700] hover:bg-[#F5C800] text-[#002A4E] font-semibold py-3 px-6 rounded-md transition-colors"
                >
                  Enregistrer tous les paramètres
                </button>
              </div>
            </div>
          )}

          {/* Logs système */}
          {activeTab === 'logs' && (
            <div className="bg-white rounded-md border border-slate-200">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-[#002A4E]">Logs système de sécurité</h2>
                <button
                  onClick={loadSystemSecurity}
                  className="text-red-600 hover:text-red-700 font-medium text-sm flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Actualiser
                </button>
              </div>
              <div className="p-6">
                {systemLogs.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Date</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Type</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Action</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Utilisateur</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">IP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {systemLogs.map((log, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 text-sm text-gray-600">
                              {new Date(log.timestamp).toLocaleString('fr-FR')}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                log.type === 'error' ? 'bg-red-100 text-red-700' :
                                log.type === 'warning' ? 'bg-orange-100 text-orange-700' :
                                log.type === 'info' ? 'bg-blue-100 text-blue-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {log.type}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-800">{log.action}</td>
                            <td className="py-3 px-4 text-sm text-gray-600">{log.user_name || 'Système'}</td>
                            <td className="py-3 px-4 text-sm font-mono text-gray-600">{log.ip_address || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <svg className="w-16 h-16 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm">Aucun journal système disponible</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </AdminLayout>
  )
}

export default SystemSecurityPage
